import styled from "styled-components";

const SIDEBAR_W = "200px";
const TOPBAR_H  = "68px";

/* ── Top bar (full width, logo + weekly header) ── */
export const TopBar = styled.header`
  position: fixed;
  top: 0; left: 0; right: 0;
  height: ${TOPBAR_H};
  display: flex;
  align-items: center;
  background: #fff;
  border-bottom: 1px solid #e0e8f0;
  z-index: 200;
`;

export const TopBarLogo = styled.div`
  width: ${SIDEBAR_W};
  min-width: ${SIDEBAR_W};
  padding: 0 18px;
  display: flex;
  align-items: center;
  height: 100%;
  border-right: 1px solid #e0e8f0;
`;

export const TopBarContent = styled.div`
  flex: 1;
  padding: 0 32px;
  display: flex;
  align-items: center;
  gap: 20px;
`;

/* ── Layout ── */
export const Layout = styled.div`
  display: flex;
  min-height: 100vh;
  background: #e8f2f7;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  padding-top: ${TOPBAR_H};
`;

export const Main = styled.main`
  margin-left: ${SIDEBAR_W};
  flex: 1;
  padding: 28px 32px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

/* ── Sidebar ── */
export const Sidebar = styled.aside`
  width: ${SIDEBAR_W};
  min-width: ${SIDEBAR_W};
  background: #fff;
  display: flex;
  flex-direction: column;
  position: fixed;
  top: ${TOPBAR_H}; left: 0; bottom: 0;
  z-index: 100;
  border-right: 1px solid #e0e8f0;
`;

export const PatientPreview = styled.div`
  padding: 14px 18px;
  display: flex;
  align-items: center;
  gap: 12px;
  border-bottom: 1px solid #e8f0f7;
  background: #f8fbfd;
`;

export const PreviewAvatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  background: #e8f0f7;
  flex-shrink: 0;
`;

export const PreviewCode = styled.span`
  color: #1b3a6b;
  font-size: 0.78rem;
  font-weight: 600;
  word-break: break-all;
`;

export const Nav = styled.nav`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding-top: 4px;
`;

export const NavItem = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 13px 18px;
  background: ${({ $active }) => ($active ? "#3d7a8a" : "none")};
  border: none;
  color: ${({ $active }) => ($active ? "#fff" : "#555")};
  font-size: 0.85rem;
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  text-align: left;
  cursor: pointer;
  width: 100%;

  &:hover {
    background: ${({ $active }) => ($active ? "#3d7a8a" : "#f0f6f8")};
    color: ${({ $active }) => ($active ? "#fff" : "#26A9B6")};
  }
`;

export const SidebarBottom = styled.div`
  background: #f5f7f9;
  border-top: 1px solid #e0e8f0;
  padding: 8px 0 4px;
`;

export const ReviewFormBtn = styled.button`
  margin: 4px 14px 8px;
  padding: 7px 12px;
  border-radius: 16px;
  border: 1.5px solid #26A9B6;
  background: #fff;
  color: #26A9B6;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;

  &:hover { background: #f0fbfc; }
`;

export const SidebarLink = styled.a`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 18px;
  color: #555;
  font-size: 0.82rem;
  text-decoration: none;
  cursor: pointer;

  &:hover { color: #26A9B6; background: #edf3f5; }
`;

export const SectionTitle = styled.h1`
  color: #2F6D92;
  font-size: 1.6rem;
  font-weight: 700;
  margin: 0;
  white-space: nowrap;
`;

export const ProgressBarWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

export const ProgressBarTrack = styled.div`
  width: 800px;
  height: 10px;
  background: #DDEFF9;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
`;

export const ProgressBarFill = styled.div`
  height: 100%;
  width: ${({ $pct }) => $pct}%;
  background: #2F6D92;
  border-radius: 8px;
  transition: width 0.3s;
`;

export const ProgressLabel = styled.span`
  font-size: 0.8rem;
  color: #888;
  white-space: nowrap;
`;

/* ── Activity type cards ── */
export const CardsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;
`;

export const ActivityCard = styled.div`
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 1px 6px rgba(0,0,0,0.07);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 300px;
  cursor: pointer;
  transition: box-shadow 0.15s, transform 0.15s;

  &:hover {
    box-shadow: 0 4px 14px rgba(0,0,0,0.13);
    transform: translateY(-2px);
  }
`;

export const CardIllustration = styled.div`
  flex: 1;
  min-height: 200px;
  background: ${({ $bg }) => $bg || "#f0f6fa"};
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const CardBody = styled.div`
  padding: 12px 14px 14px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 6px;
  flex: 1;
`;

export const CardTypeName = styled.h3`
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #1b3a6b;
`;

export const CardSubtext = styled.p`
  margin: 0;
  font-size: 0.9rem;
  color: #555;
`;

export const CardMiniBar = styled.div`
  height: 4px;
  background: #e0eaf0;
  border-radius: 4px;
  overflow: hidden;
  margin: 2px 0 4px;
`;

export const CardMiniBarFill = styled.div`
  height: 100%;
  width: ${({ $pct }) => $pct}%;
  background: #26A9B6;
  border-radius: 4px;
`;

export const CardGoalText = styled.p`
  margin: 0;
  font-size: 0.78rem;
  color: #888;
`;

export const CardButton = styled.button`
  margin-top: auto;
  padding: 8px 10px;
  border: none;
  border-radius: 8px;
  background: #79BEE6;
  color: #FFFFFF;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  width: 100%;

  &:hover { background: #37a2e0; }
`;

/* ── Bottom panels ── */
export const BottomRow = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;
`;

export const PanelWide = styled.div`
  grid-column: span 3;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 1px 6px rgba(0,0,0,0.07);
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-height: 300px;
`;

export const Panel = styled.div`
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 1px 6px rgba(0,0,0,0.07);
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-height: 300px;
`;

export const PanelTitle = styled.h2`
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: #1b3a6b;
  text-align: center;
`;

export const PanelButton = styled.button`
  margin-top: auto;
  padding: 9px;
  border: none;
  border-radius: 8px;
  background: #79BEE6;
  color: #FFFFFF;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  width: 100%;

  &:hover { background: #37a2e0; }
`;

/* Therapist message */
export const TherapistAvatar = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: #c8e6ee;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.6rem;
  margin: 0 auto;
`;

export const MessageText = styled.p`
  margin: 0;
  font-size: 0.82rem;
  color: #444;
  line-height: 1.5;
  flex: 1;
`;

/* Roadtrip */
export const WeeksRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 6px;
`;

export const WeekItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  flex: 1;
`;

export const WeekLabel = styled.span`
  font-size: 0.72rem;
  font-weight: 600;
  color: #555;
`;

export const WeekStats = styled.span`
  font-size: 0.68rem;
  color: #888;
  text-align: center;
  line-height: 1.4;
`;

/* Questionnaire */
export const QuestionnaireIcon = styled.div`
  font-size: 2.8rem;
  text-align: center;
`;

export const QuestionnaireTitle = styled.p`
  margin: 0;
  font-size: 0.9rem;
  font-weight: 700;
  color: #1b3a6b;
  text-align: center;
`;

export const QuestionnaireText = styled.p`
  margin: 0;
  font-size: 0.78rem;
  color: #666;
  text-align: center;
  line-height: 1.5;
  flex: 1;
`;
